import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material'

import { theme } from 'styles/theme'

export const ModalHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${theme.palette.neutral[700]};
  background-color: ${theme.palette.neutral[800]};
`

export const LoadingContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 40px;
`

export const LoadingText = styled(Typography)`
  color: ${theme.palette.neutral[400]};
  font-size: 16px;
`

export const ErrorContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 40px;
`

export const ErrorText = styled(Typography)`
  color: ${theme.palette.error.main};
  font-size: 16px;
  text-align: center;
`

export const EmptyContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 40px;
`

export const EmptyText = styled(Typography)`
  color: ${theme.palette.neutral[400]};
  font-size: 16px;
  text-align: center;
`

export const TableContainer = styled(Box)`
  flex: 1;
  overflow-y: hidden;
  overflow-x: hidden;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

export const Table = styled('table')`
  width: 100%;
  border-collapse: collapse;
  font-family: Inter, Roboto, sans-serif;
  table-layout: fixed;
`

export const TableHead = styled('thead')`
  position: sticky;
  top: 0;
  background-color: ${theme.palette.neutral[800]};
  z-index: 1;
`

export const TableBody = styled('tbody')``

export const TableRow = styled('tr')`
  &:nth-child(even) {
    background-color: ${theme.palette.neutral[800]};
  }

  &:hover {
    background-color: ${theme.palette.primary.main}10;
  }
`

export const TableHeaderCell = styled('th')<{ $columnType?: string }>`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${theme.palette.neutral[200]};
  border-bottom: 2px solid ${theme.palette.neutral[700]};
  white-space: nowrap;
  width: ${({ $columnType }) => {
    switch ($columnType) {
      case 'link':
        return '80px'
      case 'status':
        return '120px'
      case 'phone':
        return '140px'
      case 'currency':
        return '120px'
      case 'percent':
        return '100px'
      case 'date':
        return '120px'
      case 'short':
        return '100px'
      case 'long':
        return '250px'
      default:
        return '150px'
    }
  }};
  min-width: 100px;
  resize: horizontal;
  overflow: hidden;
  position: relative;
`

export const TableCell = styled('td')`
  padding: 12px 16px;
  font-size: 14px;
  color: ${theme.palette.neutral[300]};
  border-bottom: 1px solid ${theme.palette.neutral[800]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const ActionsHeaderCell = styled('th')<{ $actionsWidth?: number }>`
  padding: ${({ $actionsWidth }) =>
    $actionsWidth && $actionsWidth <= 45 ? '8px 4px' : '12px 16px'};
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: ${theme.palette.neutral[200]};
  border-bottom: 2px solid ${theme.palette.neutral[700]};
  white-space: nowrap;
  width: ${({ $actionsWidth = 60 }) => `${$actionsWidth}px`};
  min-width: ${({ $actionsWidth = 60 }) => `${$actionsWidth}px`};
  max-width: ${({ $actionsWidth = 60 }) => `${$actionsWidth}px`};
  position: sticky;
  right: 0;
  background-color: ${theme.palette.neutral[800]};
  z-index: 3;
`

export const ActionsCell = styled('td')<{ $actionsWidth?: number }>`
  padding: ${({ $actionsWidth }) =>
    $actionsWidth && $actionsWidth <= 45 ? '6px 4px' : '12px 12px'};
  font-size: 14px;
  color: ${theme.palette.neutral[300]};
  border-bottom: 1px solid ${theme.palette.neutral[800]};
  white-space: nowrap;
  width: ${({ $actionsWidth = 60 }) => `${$actionsWidth}px`};
  min-width: ${({ $actionsWidth = 60 }) => `${$actionsWidth}px`};
  max-width: ${({ $actionsWidth = 60 }) => `${$actionsWidth}px`};
  position: sticky;
  right: 0;
  background-color: ${theme.palette.neutral[800]};
  z-index: 2;
`

// Estilos para os filtros no estilo das cobranças
export const PopoverContainer = styled('div')(() => ({
  width: '320px',
  maxHeight: '50vh',
  display: 'flex',
  flexDirection: 'column'
}))

export const ScrollableContent = styled('div')(() => ({
  overflowY: 'auto',
  flex: 1
}))

export const FixedButtonsContainer = styled('div')(() => ({
  paddingTop: '16px',
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  display: 'flex',
  justifyContent: 'center',
  gap: 8,
  backgroundColor: theme.palette.background.paper
}))

export const AccordionSummaryContent = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%'
}))

export const TableFoot = styled('tfoot')`
  position: sticky;
  bottom: 0;
  background-color: ${theme.palette.neutral[800]};
  z-index: 2;
`

export const TableWrapper = styled('div')`
  flex: 1;
  overflow-x: auto;
  overflow-y: auto;

  /* Scrollbars mais finos e sutis (cross-browser) */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: rgba(0, 0, 0, 0.1) transparent;

  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.25);
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.35);
  }
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`

export const SummaryRow = styled('tr')`
  background-color: ${theme.palette.neutral[800]} !important;

  &:hover {
    background-color: ${theme.palette.neutral[800]} !important;
  }
`
